import type { MockBooking } from "@/types/booking";
import { operationalSourceLabel } from "@/lib/operationalDates";
import {
  earliestCutoff,
  earliestSiCyCutoff,
  flattenMatchedCutoffs,
  normalizeCutoffName,
  scheduleCutoffCaption,
  type ScheduleCutoffSource,
} from "@/lib/scheduleCutoffs";

type BookingDates = Pick<
  MockBooking,
  | "departure_date"
  | "arrival_date"
  | "cutoff_date"
  | "effective_cutoff"
  | "cutoff_source"
  | "stuffing_date"
  | "stuffing_date_source"
  | "effective_pcd"
  | "pcd_is_etd_fallback"
  | "pcd_source"
  | "pricing_date"
  | "pricing_date_is_frozen"
  | "operational"
>;

/** Operational ETD when present, otherwise the booking departure date. */
export function bookingEtd(booking: BookingDates): string | undefined {
  return booking.operational?.etd ?? booking.departure_date;
}

/** Operational ETA when present, otherwise the booking arrival date. */
export function bookingEta(booking: BookingDates): string | undefined {
  return booking.operational?.eta ?? booking.arrival_date;
}

export interface ResolvedKeyDate {
  date?: string;
  caption?: string;
  /** True when `date` includes a clock time from the schedule. */
  hasTime?: boolean;
}

/**
 * Agent cutoff, then API `effective_cutoff`, then the matched schedule's
 * SI/CY/doc cutoff (same fallback as v1 booking detail).
 */
export function resolveBookingCutoff(
  booking: BookingDates,
  schedules?: ScheduleCutoffSource[],
): ResolvedKeyDate {
  if (booking.effective_cutoff) {
    return {
      date: booking.effective_cutoff,
      caption: bookingCutoffCaption(booking),
    };
  }
  if (booking.cutoff_date) {
    return {
      date: booking.cutoff_date,
      caption: bookingCutoffCaption(booking),
    };
  }

  const cutoffs = flattenMatchedCutoffs(schedules);
  const siCy = earliestSiCyCutoff(cutoffs);
  if (siCy?.date_time) {
    return {
      date: siCy.date_time,
      caption: scheduleCutoffCaption(siCy.name),
      hasTime: /T\d{2}:\d{2}/.test(siCy.date_time),
    };
  }

  const cargo = schedules?.find((s) => s.matched)?.cargo_cutoff;
  if (cargo) {
    return {
      date: cargo,
      caption: operationalSourceLabel("schedule"),
    };
  }

  return {};
}

/** Aggregated cutoff (agent, else schedule), optionally enriched from matched sailings. */
export function bookingCutoff(
  booking: BookingDates,
  schedules?: ScheduleCutoffSource[],
): string | undefined {
  return resolveBookingCutoff(booking, schedules).date;
}

/** Extra named schedule cutoffs (e.g. DG) that are not the main cutoff row. */
export function extraScheduleCutoffRows(
  booking: BookingDates,
  schedules?: ScheduleCutoffSource[],
): Array<{ label: string; date: string; caption: string }> {
  const main = resolveBookingCutoff(booking, schedules);
  const earliest = earliestCutoff(flattenMatchedCutoffs(schedules));
  if (!earliest?.date_time) return [];
  if (main.date && sameCutoffInstant(main.date, earliest.date_time)) return [];
  return [
    {
      label: normalizeCutoffName(earliest.name) || "Schedule cutoff",
      date: earliest.date_time,
      caption: "Schedule match",
    },
  ];
}

function sameCutoffInstant(a: string, b: string): boolean {
  if (a === b) return true;
  const aDay = a.slice(0, 10);
  const bDay = b.slice(0, 10);
  return aDay === bDay && a.length === 10;
}

export function bookingStuffing(booking: BookingDates): string | undefined {
  return booking.stuffing_date;
}

/** Effective PCD (pricing anchor, else API aggregate, else ETD). */
export function bookingPcd(booking: BookingDates): string | undefined {
  return booking.pricing_date ?? booking.effective_pcd;
}

/** Caption for a fallback source — omitted when the value is the primary agent date. */
export function fallbackSourceCaption(
  source?: string | null,
): string | undefined {
  if (!source || source === "update" || source === "agent") return undefined;
  if (source === "schedule" || source === "carrier_tracking") {
    return operationalSourceLabel(source);
  }
  return undefined;
}

export function bookingCutoffCaption(
  booking: BookingDates,
): string | undefined {
  return fallbackSourceCaption(booking.cutoff_source);
}

export function bookingStuffingCaption(
  booking: BookingDates,
): string | undefined {
  return fallbackSourceCaption(booking.stuffing_date_source);
}

export function bookingPcdCaption(booking: BookingDates): string | undefined {
  // The carrier's own tracking feed (MSC PriceCalculationDate) supplied the
  // PCD — the most authoritative source, so it wins over everything else
  // (including the frozen anchor). Surface the provenance to the user.
  if (booking.pcd_source === "carrier_tracking") return "Carrier tracking";
  if (booking.pricing_date_is_frozen) return "Frozen";
  // A linked carrier invoice (ZIM INITIAL SAILING DATE) supplied the PCD because
  // the agent provided none — surface the provenance so it is visible.
  if (booking.pcd_source === "carrier_invoice") return "Carrier invoice";
  if (booking.pcd_is_etd_fallback) return "ETD fallback";
  return undefined;
}
